import SectionHeading from "@/pages/home/components/SectionHeading";
import { hotRanking, type RankItem } from "@/mocks/home";

function TrendIcon({ trend }: { trend: RankItem["trend"] }) {
  if (trend === "up") {
    return (
      <span className="flex h-4 w-4 items-center justify-center text-primary-500">
        <i className="ri-arrow-up-s-fill text-base" />
      </span>
    );
  }
  if (trend === "down") {
    return (
      <span className="flex h-4 w-4 items-center justify-center text-foreground-400">
        <i className="ri-arrow-down-s-fill text-base" />
      </span>
    );
  }
  return (
    <span className="flex h-4 w-4 items-center justify-center text-secondary-500">
      <i className="ri-subtract-line text-base" />
    </span>
  );
}

export default function HeatRanking() {
  const topThree = hotRanking.slice(0, 3);
  const rest = hotRanking.slice(3);

  return (
    <section id="ranking" className="bg-background-50">
      <div className="mx-auto max-w-[1200px] px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          index="01"
          eyebrow="Heat Ranking"
          title="31黑料网黑料排行 · 今日热度榜"
          desc="按实时热度值排序，综合搜索增量、讨论增速与二次传播系数计算。榜单只反映话题热度，不代表对事件真实性的判断。"
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
          {topThree.map((item) => (
            <article
              key={item.rank}
              className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50"
            >
              <div className="relative h-44 w-full overflow-hidden md:h-48">
                <img
                  src={item.cover}
                  alt={`${item.title} 封面`}
                  title={`${item.title} 31黑料网热门黑料`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-transparent" />
                <span className="absolute left-3 top-3 flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 font-heading text-base font-bold text-background-50">
                  {item.rank}
                </span>
                <span className="absolute right-3 top-3 rounded-full bg-background-50/95 px-2.5 py-1 text-[11px] font-medium text-foreground-800">
                  {item.category}
                </span>
                <span className="absolute bottom-3 left-3 flex items-center gap-1.5 rounded-full bg-accent-500/95 px-2.5 py-1">
                  <i className="ri-fire-fill text-xs text-accent-950" />
                  <span className="text-[11px] font-bold text-accent-950">{item.heat}</span>
                </span>
              </div>
              <div className="flex flex-1 flex-col p-4 md:p-5">
                <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 md:text-[17px]">
                  {item.title}
                </h3>
                <p className="mt-2.5 flex-1 text-[13px] leading-relaxed text-foreground-600">
                  {item.summary}
                </p>
                <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
                  <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                    <i className="ri-chat-3-line text-sm" />
                    {item.comments.toLocaleString()} 讨论
                  </span>
                  <span className="flex items-center gap-1 text-xs font-medium text-primary-600">
                    <TrendIcon trend={item.trend} />
                    {item.trend === "up" ? "上升" : item.trend === "down" ? "回落" : "持平"}
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-4 overflow-hidden rounded-lg border border-background-200 bg-background-50 md:mt-5">
          {rest.map((item, idx) => (
            <div
              key={item.rank}
              className={`flex flex-col gap-3 p-4 transition-colors hover:bg-background-100 sm:flex-row sm:items-center md:gap-5 md:px-6 ${
                idx !== rest.length - 1 ? "border-b border-background-200" : ""
              }`}
            >
              <div className="flex items-center gap-4 md:w-[54%]">
                <span className="w-8 shrink-0 font-heading text-lg font-bold text-foreground-400">
                  {String(item.rank).padStart(2, "0")}
                </span>
                <div className="min-w-0">
                  <h3 className="truncate font-heading text-[15px] font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-1 line-clamp-1 text-[13px] text-foreground-500">
                    {item.summary}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 pl-12 sm:flex-1 sm:justify-end sm:pl-0">
                <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] font-medium text-secondary-900">
                  {item.category}
                </span>
                <span className="flex items-center gap-1.5 text-xs text-foreground-500">
                  <i className="ri-chat-3-line text-sm" />
                  {item.comments.toLocaleString()}
                </span>
                <span className="flex items-center gap-1 text-xs font-medium text-foreground-700">
                  <TrendIcon trend={item.trend} />
                </span>
                <span className="w-16 text-right font-heading text-base font-bold text-accent-600">
                  {item.heat}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}